import type { Dimensions } from '@festina-life/core'
import { useCallback, useState } from 'react'

const toDimensions = (rect: DOMRect): Dimensions => rect

/**
 * Hook used for lazily retrieving the SVG dimensions of any string. This is the lazy equivalent of useSvgTextSize.
 *
 * The measuring callback supports a list as input to reduce the amount of DOM nodes created.
 */
export const useLazySvgTextSize = <T extends Element>() => {
  const [parentRef, setParentRef] = useState<T | null>(null)

  const dimensions = useCallback(
    <T extends string | string[]>(
      text: T,
    ): T extends string ? Dimensions : Dimensions[] => {
      if (!parentRef) {
        throw new Error(
          'A container element has not been mounted to measure the dimensions of SVG text.',
        )
      }

      const svgNS = 'http://www.w3.org/2000/svg'
      const newText = document.createElementNS(svgNS, 'text')
      const textNode = document.createTextNode(
        typeof text === 'string' ? text : '',
      )
      newText.appendChild(textNode)

      parentRef.appendChild(newText)

      const result =
        typeof text === 'string'
          ? toDimensions(newText.getBoundingClientRect())
          : text.map((string) => {
              newText.textContent = string

              return toDimensions(newText.getBoundingClientRect())
            })

      parentRef.removeChild(newText)

      return result as T extends string ? Dimensions : Dimensions[]
    },
    [parentRef],
  )

  return { dimensions, svgRef: setParentRef, didMount: !!parentRef }
}
